import { mapState } from 'vuex';
import styled, { css } from 'vue-styled-components';
import {
    COLORS,
    square,
    applyHoverAndPress,
    BLUE,
    RespondMobile,
} from '@styles/common.styled';
import iframeMixin from '@/mixins/iframe';
import { debounce, sumBy } from 'lodash';
import Star from '@svgIcons/uac_star_icon.svg';

export const FLEX_DIV = styled.div`
    display: flex;
`;

export const FLEX_VERTICAL_CENTER_DIV = styled(FLEX_DIV)`
    align-items: center;
`;

export const FLEX_COLUMN_DIV = styled(FLEX_DIV)`
    flex-direction: column;
`;

export const FLEX_CENTER_DIV = styled(FLEX_VERTICAL_CENTER_DIV)`
    justify-content: center;
`;

export const GRAY_100_SPAN = styled.span`
    color: ${COLORS['gray-100']};
`;

export const GRAY_100_DIV = styled.div`
    color: ${COLORS['gray-100']};
`;

export const DIALOG_TEXT = styled(GRAY_100_SPAN)`
    font-size: 16px;
    max-width: 450px;
    display: inline-block;
    white-space: pre-line;
    @media screen and (max-width: 767px) {
        font-size: 18px;
    }
`;

export const OUTER_WRAPPER = styled.div`
    min-height: 100vh;
    background: white;
    padding-left: 5%;
    padding-right: 5%;
    @media screen and (min-width: 768px) {
        min-width: 1280px;
    }
`;

export const INNER_WRAPPER = styled.div`
    width: 100%;
    max-width: 1600px;
    padding-top: 112px;
    padding-bottom: 140px;
    margin: 0 auto;
    @media screen and (max-width: 1000px) {
        min-width: auto;
        padding-top: 72px;
        width: 100%;
        padding-bottom: 110px;
    }

    @media screen and (min-width: 1001px) {
        max-width: 1600px;
        padding-top: 112px;
    }
`;

export const BOTTOM_CONTAINER = styled(FLEX_VERTICAL_CENTER_DIV)`
    position: fixed;
    justify-content: center;
    margin-top: 30px;
    width: 100%;
    bottom: 0px;
    height: 64px;
    border: 1px solid #d4d8db;
    background-color: white;
    align-items: center;
    z-index: 11;
    left: 0;
    right: 0;
    padding-left: 5%;
    padding-right: 5%;

    @media screen and (max-width: 767px) {
        min-width: auto;
        width: 100%;
    }
`;

export const BOTTOM_CONTENTS_AREA = styled(FLEX_DIV)`
    max-width: 1600px;
    justify-content: space-between;
    width: 100%;
    @media screen and (max-width: 767px) {
        min-width: auto;
    }
`;

export const TOOLTIP_CONTENT = styled(GRAY_100_DIV, { size: String })`
    max-width: 360px;
    font-size: 13px;
    min-width: ${({ size }) => (size === 'small' ? '50px' : '200px')};
    white-space: pre-line;
    word-break: keep-all;
    @media screen and (max-width: 767px) {
        max-width: 300px;
    }
`;

export const FLEX_COLUMN_ALIGN_CENTER_DIV = styled(FLEX_COLUMN_DIV)`
    align-items: center;
`;

const DEFAULT_POSITION = {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
};

export const ICON_WRAPPER = styled(FLEX_CENTER_DIV, {
    long: Number,
    iconSize: Number,
    fillColor: String,
})`
    ${({ long = 24 }) => square(long)};
    transition: 0.2s background-color;
    cursor: pointer;
    border-radius: 2px;
    svg {
        margin: 0 !important;
        ${({ iconSize }) =>
            iconSize &&
            css`
                ${square(iconSize)};
            `};
        ${({ fillColor }) =>
            fillColor && applyHoverAndPress(fillColor, 'fill')};

        path {
            ${({ fillColor }) =>
                fillColor && applyHoverAndPress(fillColor, 'fill')};
        }
    }

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
`;

export const make = ({ base, styles = [] }) =>
    styles.filter(R.identity).reduce(
        (acc, current) =>
            styled(acc)`
                ${current};
            `,
        base
    );

const StarWrapper = styled.span`
    padding: 0 3px;

    svg {
        width: 13px;
        height: 13px;

        path {
            fill: ${BLUE(60)};
        }
    }
`;

export const StarIcon = () => (
    <StarWrapper>
        <Star v-view-box />
    </StarWrapper>
);

export const VERTICAL_SPACER = styled('div', { amount: Number })`
    width: 100%;
    height: ${({ amount = 0 }) => `${amount}px`};
`;

export const HORIZONTAL_SPACER = styled('div', { amount: Number })`
    height: 1px;
    width: ${({ amount = 0 }) => `${amount}px`};
`;

export const WIDTH_100_DIV = styled.div`
    width: 100%;
`;

export const PageLoaderWrapper = styled(FLEX_CENTER_DIV)`
    width: 100%;
    height: 100vh;
`;

const IframeWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
`;

const StyledIframe = styled('iframe', { noMinHeight: Boolean })`
    display: block;
    user-select: none;
    ${({ noMinHeight }) =>
        noMinHeight !== true &&
        css`
            min-height: 600px;
            /* ${RespondMobile`
                min-height: unset;
            `} */
        `}
`;

const IframeOverlay = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
`;

const getHasActiveOverlay = () => {
    const selectors = ['.adriel-dialog', '.ant-drawer-open'];

    return selectors.some(
        (selector) => document.querySelectorAll(selector).length > 0
    );
};

export const Iframe = {
    inheritAttrs: true,
    mixins: [iframeMixin],
    props: {
        src: {
            type: String,
        },
        resizing: {
            type: Boolean,
            default: true,
        },
        customWidth: String | Number,
        customHeight: String | Number,
        noMinHeight: {
            type: Boolean,
            default: false,
        },
        overlayVisible: {
            type: Boolean,
            default: true,
        },
        iframeKey: String,
    },
    data() {
        return {
            hasActiveOverlay: false,
            intervalId: null,
            iframeWidth: 0,
            iframeHeight: 0,
            iframeListeners_: this.resizing
                ? [
                      {
                          eventType: `PAGE_SIZE_UPDATED`,
                          listener: (event) => {
                              this.handleIframeResize(event);
                          },
                      },
                  ]
                : [],
        };
    },
    computed: {
        ...mapState('iframe', ['rectFromReact']),
        ...mapState('newDashboard', ['hubspotBannerHeight']),
        height() {
            return this.iframeHeight;
        },
        width() {
            return this.iframeWidth;
        },
        refinedHubspotBannerHeight() {
            const { hubspotBannerHeight } = this;
            return hubspotBannerHeight ?? 0;
        },
        positionLeftTop() {
            const {
                rectFromReact,
                windowDimension,
                refinedHubspotBannerHeight,
            } = this;
            if (!rectFromReact) return DEFAULT_POSITION;
            const { width: windowWidth, height: windowHeight } =
                windowDimension;
            const { bottom, left } = rectFromReact;
            return {
                top: `${refinedHubspotBannerHeight}px`,
                right: `${Math.max(0, windowWidth - left)}px`,
                bottom: `${Math.max(
                    0,
                    windowHeight - bottom - refinedHubspotBannerHeight
                )}px`,
                left: '0px',
                width: 'unset',
                height: 'unset',
            };
        },
        positionRightTop() {
            const {
                rectFromReact,
                windowDimension,
                refinedHubspotBannerHeight,
            } = this;
            if (!rectFromReact) return DEFAULT_POSITION;
            const { left, top } = rectFromReact;
            const { height: windowHeight } = windowDimension;
            return {
                top: `${refinedHubspotBannerHeight}px`,
                right: `0px`,
                bottom: `${Math.max(
                    0,
                    windowHeight - top - refinedHubspotBannerHeight
                )}px`,
                left: `${left}px`,
                width: 'unset',
                height: 'unset',
            };
        },
        positionRightBottom() {
            const { rectFromReact, refinedHubspotBannerHeight } = this;
            if (!this.rectFromReact) return DEFAULT_POSITION;
            const { right, top } = rectFromReact;
            return {
                top: `${top + refinedHubspotBannerHeight}px`,
                right: `0px`,
                bottom: `0px`,
                left: `${right}px`,
                width: 'unset',
                height: 'unset',
            };
        },
        positionLeftBottom() {
            const {
                rectFromReact,
                windowDimension,
                refinedHubspotBannerHeight,
            } = this;
            if (!this.rectFromReact) return DEFAULT_POSITION;
            const { right, bottom } = rectFromReact;
            const { width: windowWidth } = windowDimension;
            return {
                top: `${bottom + refinedHubspotBannerHeight}px`,
                right: `${Math.max(0, windowWidth - right)}px`,
                bottom: `0px`,
                left: `0px`,
                width: 'unset',
                height: 'unset',
            };
        },
    },
    mounted() {
        const globalOverlayIntervalMillis = 1000;
        this.intervalId = setInterval(() => {
            this.hasActiveOverlay = getHasActiveOverlay();
        }, globalOverlayIntervalMillis);

        if (!this.resizing) return;

        window.addEventListener('resize', this.handleWindowResize);
    },
    beforeDestroyed() {
        clearTimeout(this.intervalId);
        if (!this.resizing) return;
        window.removeEventListener('resize', this.handleWindowResize);
    },
    methods: {
        handleWindowResize() {
            this.iframeWidth = R.path(
                ['$refs', 'wrapper', '$el', 'clientWidth'],
                this
            );
        },
        handleIframeResize: debounce(function (event) {
            const { height: iframeHeight, width: iframeWidth } = event.data;

            if (iframeHeight === 0) {
                return;
            }
            const safePaddingPx = 1;
            this.iframeHeight = iframeHeight + safePaddingPx;
            const wrapperWidth = R.path(
                ['$refs', 'wrapper', '$el', 'clientWidth'],
                this
            );

            this.iframeWidth = wrapperWidth
                ? Math.max(iframeWidth, wrapperWidth)
                : iframeWidth;
        }, 1000),
    },
    render() {
        return (
            <IframeWrapper ref="wrapper">
                <StyledIframe
                    ref="iframe"
                    class={`react-iframe ${this.iframeKey}`}
                    allow="clipboard-write"
                    style={{
                        width: this.customWidth || `${this.width}px`,
                        height: this.customHeight || `${this.height}px`,
                    }}
                    src={this.src}
                    noMinHeight={this.noMinHeight}
                />
                {this.hasActiveOverlay && this.overlayVisible && (
                    <IframeOverlay
                        class="iframe-overlay"
                        style={{ ...this.positionLeftTop }}
                    />
                )}
                {this.hasActiveOverlay && this.overlayVisible && (
                    <IframeOverlay
                        class="iframe-overlay"
                        style={{
                            ...this.positionRightTop,
                        }}
                    />
                )}
                {this.hasActiveOverlay && this.overlayVisible && (
                    <IframeOverlay
                        class="iframe-overlay"
                        style={{
                            ...this.positionRightBottom,
                        }}
                    />
                )}
                {this.hasActiveOverlay && this.overlayVisible && (
                    <IframeOverlay
                        class="iframe-overlay"
                        style={{
                            ...this.positionLeftBottom,
                        }}
                    />
                )}
            </IframeWrapper>
        );
    },
};
